import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import './ToolPage.css'
import './RetroBoard.css'
import RetroColumn from './RetroColumn'
import {
  fetchRetroState,
  retroStreamUrl,
  addRetroCard,
  removeRetroCard,
  setRetroCardReaction,
  addRetroComment,
  removeRetroComment,
} from './utils/retroApiClient'
import { loadReactedIds, saveReactedIds } from './utils/retroReactions'
import { getVisitorId } from './utils/retroVisitor'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const COLUMNS = [
  { key: 'good', title: 'O que foi bom' },
  { key: 'bad', title: 'O que foi ruim' },
  { key: 'improve', title: 'O que melhorar' },
]

const mineKey = (boardId) => `retro-board-${boardId}-mine`
const myCommentsKey = (boardId) => `retro-board-${boardId}-my-comments`

const RetroBoardView = () => {
  useDocumentMeta({
    title: 'Retro ao Vivo | jarder.dev',
    description: 'Poste um cartão anônimo e acompanhe o quadro da equipe em tempo real.',
  })

  const { id } = useParams()
  const [state, setState] = useState(null)
  const [status, setStatus] = useState('loading')
  const [error, setError] = useState('')
  // Maps cardId -> authorToken for cards this browser wrote, so it can
  // offer a delete button just for those — persisted locally since there's
  // no login/session at all to track this server-side.
  const [mine, setMine] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(mineKey(id)) || '{}')
    } catch {
      return {}
    }
  })
  const [reactedIds, setReactedIds] = useState(() => loadReactedIds(id))
  // Maps commentId -> authorToken for comments this browser wrote (same
  // idea as `mine` above, just for comments instead of whole cards).
  const [myComments, setMyComments] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(myCommentsKey(id)) || '{}')
    } catch {
      return {}
    }
  })

  useEffect(() => {
    let cancelled = false

    fetchRetroState(id)
      .then((data) => {
        if (cancelled) return
        setState(data)
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('notfound')
      })

    const eventSource = new EventSource(retroStreamUrl(id))
    eventSource.onmessage = (event) => {
      setState(JSON.parse(event.data))
    }

    return () => {
      cancelled = true
      eventSource.close()
    }
  }, [id])

  const addCard = async (column, text) => {
    setError('')
    try {
      const result = await addRetroCard(id, column, text)
      setMine((current) => {
        const next = { ...current, [result.id]: result.authorToken }
        localStorage.setItem(mineKey(id), JSON.stringify(next))
        return next
      })
      trackEvent('tool_generate', { tool: 'retro_board_add_card', column })
    } catch (err) {
      setError(err.message || 'Não foi possível enviar o cartão.')
    }
  }

  const removeCard = async (card) => {
    setError('')
    try {
      await removeRetroCard(id, card.id, { authorToken: mine[card.id] })
      setMine((current) => {
        const next = { ...current }
        delete next[card.id]
        localStorage.setItem(mineKey(id), JSON.stringify(next))
        return next
      })
      trackEvent('tool_generate', { tool: 'retro_board_remove_own_card' })
    } catch (err) {
      setError(err.message || 'Não foi possível excluir o cartão.')
    }
  }

  const canRemove = (card) => Object.hasOwn(mine, card.id)

  const toggleReact = async (card) => {
    setError('')
    const alreadyReacted = reactedIds.has(card.id)
    try {
      await setRetroCardReaction(id, card.id, getVisitorId(), !alreadyReacted)

      setReactedIds((current) => {
        const next = new Set(current)
        if (alreadyReacted) next.delete(card.id)
        else next.add(card.id)
        saveReactedIds(id, next)
        return next
      })
      trackEvent('tool_generate', { tool: 'retro_board_react' })
    } catch (err) {
      setError(err.message || 'Não foi possível reagir.')
    }
  }

  const comment = async (card, text) => {
    setError('')
    try {
      const result = await addRetroComment(id, card.id, text)
      setMyComments((current) => {
        const next = { ...current, [result.id]: result.authorToken }
        localStorage.setItem(myCommentsKey(id), JSON.stringify(next))
        return next
      })
      trackEvent('tool_generate', { tool: 'retro_board_comment' })
    } catch (err) {
      setError(err.message || 'Não foi possível comentar.')
    }
  }

  const removeComment = async (card, commentId) => {
    setError('')
    try {
      await removeRetroComment(id, card.id, commentId, { authorToken: myComments[commentId] })
      setMyComments((current) => {
        const next = { ...current }
        delete next[commentId]
        localStorage.setItem(myCommentsKey(id), JSON.stringify(next))
        return next
      })
      trackEvent('tool_generate', { tool: 'retro_board_remove_own_comment' })
    } catch (err) {
      setError(err.message || 'Não foi possível excluir o comentário.')
    }
  }

  const canRemoveComment = (item) => Object.hasOwn(myComments, item.id)

  if (status === 'loading') {
    return (
      <div className="tool-page">
        <p>Carregando...</p>
      </div>
    )
  }

  if (status === 'notfound') {
    return (
      <div className="tool-page">
        <Link to="/ferramentas" className="tool-back-link">
          ← Todas as ferramentas
        </Link>
        <h1>Quadro não encontrado</h1>
        <p className="tool-disclaimer">Esse quadro não existe ou expirou.</p>
      </div>
    )
  }

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Retro ao Vivo</h1>
      <p className="tool-disclaimer">
        Poste um cartão anônimo em qualquer coluna. Não precisa de nome,
        e todo mundo vê o quadro atualizado na hora. Você pode excluir os
        cartões e comentários que você mesmo postou.
      </p>

      {error && <p className="tool-error">{error}</p>}

      <div className="retro-board">
        {COLUMNS.map((col) => (
          <RetroColumn
            key={col.key}
            title={col.title}
            column={col.key}
            cards={state.cards.filter((card) => card.column === col.key)}
            onAdd={addCard}
            canRemove={canRemove}
            onRemove={removeCard}
            reactedIds={reactedIds}
            onToggleReact={toggleReact}
            onComment={comment}
            isMaster={false}
            canRemoveComment={canRemoveComment}
            onRemoveComment={removeComment}
          />
        ))}
      </div>
    </div>
  )
}

export default RetroBoardView
